"use client";

import { useState } from "react";
import { PlayCircle } from "lucide-react";

import {
  getVideoThumbnailUrl,
  isDirectVideoUrl,
  uploadedThumbUrl,
} from "@/lib/resources/videoThumbnail";

// A compact video preview for resource lists/grids. Resolution order:
//   1. static still (YouTube) or upload-time poster (<videoUrl>.thumb.jpg)
//   2. <video> first-frame poster — for direct videos with no generated poster
//      (older uploads); #t seeks past a likely-black opening frame
//   3. play-button placeholder — for video hosts with no derivable still
//      (Vimeo/Loom/Wistia) or when an image fails to load
//
// The caller sizes it via className (e.g. "h-9 w-16" in the list, "h-full
// w-full" inside an aspect box in the grid).
export default function ResourceThumbnail({ resource, className = "" }) {
  const url = resource?.url || "";
  const staticThumb = getVideoThumbnailUrl(url);
  const directVideo = isDirectVideoUrl(url);
  const candidate = staticThumb || (directVideo ? uploadedThumbUrl(url) : null);

  const [imgFailed, setImgFailed] = useState(false);
  const showImg = Boolean(candidate) && !imgFailed;

  return (
    <div className={`relative overflow-hidden bg-slate-900 ${className}`}>
      {showImg ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={candidate}
          alt=""
          loading="lazy"
          onError={() => setImgFailed(true)}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : directVideo ? (
        <video
          src={`${url.split("#")[0]}#t=0.5`}
          muted
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : null}
      <span className="pointer-events-none absolute inset-0 grid place-items-center">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-white/90 shadow">
          <PlayCircle className="h-4 w-4 translate-x-px text-slate-900" />
        </span>
      </span>
    </div>
  );
}
